import { useQuery } from '@apollo/client';
import { usePaginationSearch } from '@application/utils';
import { graphql } from '@gql/gql';
import type { EducatorGetReviewsQuery, EducatorGetReviewsQueryVariables } from '@gql/graphql';
import { SortOrder } from '@gql/graphql';
import { useMemo } from 'react';
import { REVIEWS_PAGINATED_SIZE } from '../constants';

export const PROFESSIONAL_GET_REVIEWS = graphql(`
  query EducatorGetReviews(
    $where: EducatorReviewWhereInput
    $take: Int
    $skip: Int
    $professionalWhere: ProfessionalWhereInput
    $orderBy: EducatorReviewOrderByWithRelationAndSearchRelevanceInput!
  ) {
    searchProfessionals(where: $professionalWhere, take: 1) {
      professionals {
        id
        reviews(take: $take, skip: $skip, where: $where, orderBy: $orderBy) {
          id
          createdAt
          grade
          verified
          description
          account {
            id
            firstName
            lastName
            imgs {
              id
              small
            }
          }
        }
      }
    }
    aggregateEducatorReview(where: $where) {
      _count {
        _all
      }
      _avg {
        grade
      }
    }
  }
`);

export const useGetEducatorReviews = (professionalId?: number) => {
  const { currentPage } = usePaginationSearch();

  const variables: EducatorGetReviewsQueryVariables = useMemo(() => {
    return {
      orderBy: { createdAt: SortOrder.desc },
      take: REVIEWS_PAGINATED_SIZE,
      skip: currentPage * REVIEWS_PAGINATED_SIZE,
      professionalWhere: { id: { equals: professionalId } },
      where: {
        professionalId: { equals: professionalId },
      },
    } as EducatorGetReviewsQueryVariables;
  }, [professionalId, currentPage]);

  const { data, error, loading, refetch } = useQuery<
    EducatorGetReviewsQuery,
    EducatorGetReviewsQueryVariables
  >(PROFESSIONAL_GET_REVIEWS, {
    variables,
    skip: professionalId == null,
  });
  return {
    educatorReviews: data?.searchProfessionals?.professionals[0]?.reviews,
    educatorReviewsAggregate: data?.aggregateEducatorReview,
    educatorReviewsError: error,
    educatorReviewsLoading: loading,
    educatorReviewsVariables: variables,
    educatorReviewsRefetch: refetch,
  };
};
